import React from 'react';
import {
  BarChart2, Layers, LayoutDashboard, BarChart3, LineChart, PieChart, Activity, Users,
  MousePointerClick, Search, FileText, Building2, Gauge, Target, Sparkles
} from 'lucide-react';
import './ChoixDashboardAnalytics.css';
import pianoLogo from '../logos/piano-analytics.png';
import matomoLogo from '../logos/matomo.png';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';

/**
 * Le choix entre le dashboard d'une solution de mesure et le consolidé.
 *
 * ## Pourquoi cet écran existe
 *
 * L'accueil portait deux tableaux — « Consolidé » et « Analytics » — qui lisaient les mêmes
 * données par le même appel, avec les mêmes colonnes par défaut et les mêmes fonctions de
 * cellule. Ils ne différaient que par la destination de leur bouton « Voir ». C'était poser la
 * question du dashboard en doublant tous les chiffres, sur toute la hauteur de la page.
 *
 * Elle se pose désormais **une fois, et là où elle se pose vraiment** : après le clic, quand on
 * sait de quelle ligne on parle.
 *
 * ## Le gabarit
 *
 * Celui du hub des termes de recherche (`ToolsView`, `#tools/negative-keywords-hub`) : en-tête
 * centré, cartes en grille, carte entière cliquable et non un bouton à l'intérieur. Les classes
 * sont recopiées plutôt qu'importées de `ToolsView.css` — ce fichier pèse quelques milliers de
 * lignes dont cet écran n'a que faire, et les charger pour deux cartes entraînerait tout le
 * reste.
 */

/**
 * Ce qui identifie une source à l'écran.
 *
 * ⚠️ **Cet écran ne sert plus les seules solutions de mesure**, malgré son nom de fichier : une
 * régie peut porter un dashboard sur mesure, et il lui faut une porte. Le nom du composant est
 * une dette assumée — le renommer toucherait son import, sa feuille de style et un écran que
 * deux sessions modifient en ce moment.
 *
 * ⚠️ **Y figurer, c'est pouvoir porter un dashboard sur mesure.** `ListeComptes` dérive de cette
 * table les comptes à qui proposer la modale : une source absente d'ici n'aurait aucune porte
 * pour ouvrir ce qu'on y hébergerait. Les deux listes ne peuvent donc pas diverger.
 *
 * `description` ne sert qu'aux sources dont le tableau n'est pas évident ; la carte retombe sur
 * une phrase générique.
 */
const SOLUTIONS = {
  /*
   * ⚠️ Y figurer a un **effet de bord voulu** : `SOURCES_AVEC_DASHBOARD` en dérive, et une
   * boutique devient donc éligible aux dashboards sur mesure. C'est souhaitable — autant le
   * savoir plutôt que de le découvrir.
   */
  PRESTASHOP: {
    nom: 'PrestaShop',
    logo: null,
    description: "Le chiffre d'affaires réel de la boutique : commandes encaissées, panier "
      + 'moyen, décomposition du CA et meilleures ventes.'
  },
  GOOGLE_ANALYTICS: {
    nom: 'Google Analytics',
    logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png',
    description: "L'audience mesurée par Google Analytics : sessions, pages, canaux "
      + 'd\'acquisition, conversions et parcours.'
  },
  PIANO_ANALYTICS: {
    nom: 'Piano Analytics',
    logo: pianoLogo,
    description: "L'audience mesurée par Piano Analytics : visites, pages, sources et "
      + 'conversions.'
  },
  MATOMO: {
    nom: 'Matomo',
    logo: matomoLogo,
    description: "L'audience mesurée par Matomo : sessions, pages, canaux d'acquisition, "
      + 'conversions et parcours.'
  },
  GOOGLE_ADS: { nom: 'Google Ads', logo: googleAdsLogo },
  MICROSOFT_ADS: { nom: 'Microsoft Ads', logo: microsoftAdsLogo },
  FACEBOOK_ADS: { nom: 'Meta Ads', logo: metaAdsLogo },
  PINTEREST_ADS: { nom: 'Pinterest Ads', logo: pinterestAdsLogo },
  TIKTOK_ADS: { nom: 'TikTok Ads', logo: tiktokAdsLogo },
  OPENAI_ADS: { nom: 'ChatGPT Ads', logo: chatgptAdsLogo },
  AWIN: { nom: 'Awin', logo: awinLogo }
};

/** Les sources qui peuvent porter un dashboard sur mesure, puisqu'il existe une porte. */
export const SOURCES_AVEC_DASHBOARD = Object.keys(SOLUTIONS);

/**
 * Les icônes qu'une carte sur mesure peut porter.
 *
 * ⚠️ **Une table explicite, et non `import * as Icones from 'lucide-react'`.** Le namespace
 * paraissait plus souple — n'importe quel nom d'icône aurait fonctionné — mais il embarque le jeu
 * entier dans le paquet : mesuré, **+146 ko gzippés** sur un paquet déjà signalé comme trop gros
 * par la compilation. La liste est de toute façon fermée côté administration ; elle doit l'être
 * ici aussi, et pour la même raison qu'ailleurs : un champ libre laisserait la carte porter
 * n'importe quoi.
 *
 * Les deux listes se tiennent la main : `ICONES` dans `ModaleDashboardsSurMesure.jsx` propose, et
 * celle-ci rend. Un nom proposé et non rendu retombe sur l'icône par défaut — sans faire
 * disparaître la carte, ce qui serait le pire des deux.
 */
const ICONES_SUR_MESURE = {
  LayoutDashboard, BarChart3, LineChart, PieChart, Activity, Users,
  MousePointerClick, Search, FileText, Building2, Gauge, Target
};

const IconeSurMesure = ({ nom, ...props }) => {
  const Icone = (nom && ICONES_SUR_MESURE[nom]) || LayoutDashboard;
  return <Icone {...props} />;
};

const ChoixDashboardAnalytics = ({
  typeSource,
  siteId = null,
  nomDuCompte = null,
  onChoisirAnalytics,
  onChoisirConsolide,
  // Les dashboards sur mesure disponibles pour ce compte. La liste est déjà bornée par le
  // serveur — droit client et `visibleClient` compris : cet écran ne filtre rien.
  dashboardsSurMesure = [],
  onChoisirSurMesure,
  /**
   * Ouvre la génération assistée, ou `null` si elle n'est pas offerte.
   *
   * ⚠️ **C'est le second accès, et il existe parce que le premier était introuvable.** La
   * génération n'était atteignable que par la 2ᵉ de 4 icônes d'une ligne de Comptes, dans une
   * modale, sur 3 types de compte sur 13 : l'auteur de la fonctionnalité ne l'a pas trouvée en
   * trois essais. Ici, elle est là où la question se pose — l'écran qui demande quel tableau de
   * bord ouvrir, et qui liste déjà les dashboards sur mesure.
   *
   * Celui de Comptes reste : c'est là que vivent les réglages d'un compte, et c'est le seul
   * endroit où l'on puisse en **coller** un.
   */
  onGenerer = null,
  onRetour,
  // Deux écrans mènent ici — l'accueil et le dashboard : le bouton doit nommer celui d'où
  // l'on vient, sans quoi il annonce un déplacement qu'il ne fait pas.
  libelleRetour = "Retour à l'accueil"
}) => {
  const solution = SOLUTIONS[typeSource];

  /**
   * Une solution inconnue ne propose rien.
   *
   * Le cas ne devrait pas se produire — l'accueil ne transmet que les trois types qu'il
   * connaît — mais un écran de choix entre deux destinations qu'on ne sait pas construire
   * vaudrait moins qu'un retour en arrière.
   */
  if (!solution) {
    return (
      <div className="choix-dashboard">
        <div className="choix-dashboard-entete">
          <BarChart2 size={32} />
          <h1>Source inconnue</h1>
          <p>Cet écran ne sait pas quels dashboards proposer pour cette source.</p>
        </div>
        <button className="choix-dashboard-retour" onClick={onRetour}>{libelleRetour}</button>
      </div>
    );
  }

  return (
    <div className="choix-dashboard">
      <div className="choix-dashboard-entete">
        <img src={solution.logo} alt={solution.nom} className="choix-dashboard-logo-entete" />
        <h1>{nomDuCompte || solution.nom}</h1>
        <p>Quel tableau de bord voulez-vous ouvrir&nbsp;?</p>
      </div>

      <div className="choix-dashboard-grille">
        {/* La carte entière est cliquable, comme dans le hub des outils : un bouton à
            l'intérieur ferait viser une cible de la taille d'un mot. */}
        <div
          className="choix-dashboard-carte"
          onClick={() => onChoisirAnalytics(typeSource, siteId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChoisirAnalytics(typeSource, siteId); } }}
        >
          <img src={solution.logo} alt={solution.nom} className="choix-dashboard-logo" />
          <h3>Dashboard {solution.nom}</h3>
          <p>
            {solution.description
              || `Les performances de ${solution.nom} : dépense, impressions, clics et conversions,
                  campagne par campagne.`}
          </p>
        </div>

        {/*
          ⚠️ Le consolidé croise une **audience** avec les régies : sur un compte de régie, il
          serait l'un des deux termes du croisement, et la carte mènerait à un écran vide. C'est
          `destinationsDuChoix` qui tranche côté App ; ici on ne rend la carte que si l'appelant
          a câblé le geste.
        */}
        {onChoisirConsolide && (
          <div
            className="choix-dashboard-carte"
            onClick={() => onChoisirConsolide(typeSource, siteId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChoisirConsolide(typeSource, siteId); } }}
          >
            <span className="choix-dashboard-icone"><Layers size={64} /></span>
            <h3>Dashboard consolidé</h3>
            <p>
              La même audience croisée avec les régies publicitaires : coût, COS et CPA par canal,
              budgets saisis et atterrissage du mois.
            </p>
          </div>
        )}

        {/*
          Les dashboards sur mesure sont des cartes de la **même grille**, à la suite des deux
          autres — sans intertitre qui les sépare.

          Ils ont eu leur grille et leur titre de famille. Deux raisons de l'avoir défait : une
          grille propre en `auto-fill` donnait quatre colonnes, donc une carte seule deux fois
          plus étroite et bien plus haute que ses voisines, alors que le cas courant est d'en
          avoir une ; et sans titre entre elles, deux grilles distinctes se toucheraient, l'écart
          de 24 px n'existant qu'*à l'intérieur* d'une grille. Une seule grille règle les deux, et
          la question posée à l'écran reste une : quel tableau de bord ouvrir.
        */}
        {/*
          La carte de génération, **en dernier**, et dessinée autrement : bordure en pointillés,
          pas de logo. Les autres cartes *ouvrent* quelque chose qui existe ; celle-ci en
          *fabrique* un. Lui donner le même aplat la ferait lire comme un dashboard de plus, et
          l'on cliquerait dessus en croyant ouvrir.
        */}
        {onGenerer && (
          <div
            className="choix-dashboard-carte choix-dashboard-creer"
            onClick={onGenerer}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGenerer(); } }}
          >
            <span className="choix-dashboard-icone"><Sparkles size={64} /></span>
            <h3>Générer un dashboard sur mesure</h3>
            <p>
              Décrivez ce que le tableau doit montrer. Claude explore les données de ce compte,
              écrit le fichier, et vous le prévisualisez avant de le publier.
            </p>
          </div>
        )}

        {dashboardsSurMesure.map((d) => (
          <div
            key={d.id}
            className="choix-dashboard-carte"
            onClick={() => onChoisirSurMesure(d.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChoisirSurMesure(d.id); } }}
          >
            <span className="choix-dashboard-icone">
              <IconeSurMesure nom={d.icone} size={64} />
            </span>
            <h3>{d.nom}</h3>
            <p>{d.description || d.sousTitre || `Tableau de bord sur mesure, ${solution.nom}.`}</p>
          </div>
        ))}
      </div>

      <button className="choix-dashboard-retour" onClick={onRetour}>{libelleRetour}</button>
    </div>
  );
};

export default ChoixDashboardAnalytics;
